<template>
    <div class="llm-configuration">
        <div class="llm-button-group">
            <!-- Model Seçici Butonu -->
            <div class="llm-selector-btn" @click="openModelSelector">
                <div class="btn-content">
                    <div class="model-info">
                        <i class="material-icons model-icon">psychology</i>
                        <div class="model-name">{{ selectedModelName }}</div>
                    </div>
                    <i class="material-icons">expand_more</i>
                </div>
            </div>

            <!-- Model Ayarları Butonu -->
            <a-popover
                placement="topLeft"
                :visible="isSettingsVisible"
                @visibleChange="handleSettingsVisibleChange"
                :trigger="['click']"
                overlayClassName="llm-settings-popover"
            >
                <template slot="content">
                    <div class="llm-settings">
                        <h3 class="settings-title">Model Ayarları</h3>

                        <!-- LLM Temperature -->
                        <div class="settings-section">
                            <div class="slider-header">
                                <h4 class="section-title">LLM Temperature</h4>
                                <span class="value">{{ formatTemperature(localModelTemperature) }}</span>
                            </div>
                            <a-slider v-model="localModelTemperature" :min="0" :max="1" :step="0.01" />
                            <div class="slider-description">
                                Düşük değer daha belirleyici, yüksek değer daha rastgele yanıtlar üretir. [0-1]
                            </div>
                        </div>

                        <!-- Structured Output -->
                        <div class="settings-section">
                            <div class="setting-checkbox">
                                <h4 class="section-title">Structured Output</h4>
                                <a-switch v-model="localToolCallStrictMode" />
                            </div>
                            <div class="setting-description">
                                Araç çağrısı argümanlarının belirttiğiniz JSON şemasına uymasını sağlamak için
                                yapılandırılmış çıktı kullanır. Bu, araçları kaydetme veya değiştirme süresini
                                uzatabilir.
                            </div>
                        </div>

                        <!-- Butonlar -->
                        <div class="action-buttons">
                            <a-button @click="resetSettings">İptal</a-button>
                            <PermissionController :permissions="['voice-agent.update']">
                                <a-button type="primary" @click="saveSettings">Kaydet</a-button>
                            </PermissionController>
                        </div>
                    </div>
                </template>
                <div class="llm-settings-btn">
                    <i class="material-icons">settings</i>
                </div>
            </a-popover>
        </div>

        <!-- Model Seçici Modal (overlayClassName ile body'de render edildiğinde stiller uygulanır) -->
        <a-modal
            v-model="isModelSelectorVisible"
            title="Model Seçimi"
            :footer="null"
            :width="1000"
            wrapClassName="llm-model-selector-modal"
            @cancel="isModelSelectorVisible = false"
        >
            <div class="model-selector llm-model-selector-modal-content">
                <div class="model-columns">
                    <!-- OpenAI Models -->
                    <div class="model-column">
                        <div class="column-header">
                            <div class="provider-logo-large">
                                <img src="https://static.supsis.live/common/image/openai.svg" alt="OpenAI" />
                            </div>
                            <h3 class="column-title">OpenAI</h3>
                        </div>
                        <div class="model-list">
                            <div v-for="model in modelOptions.openai" :key="model.id">
                                <!-- Default Tier -->
                                <div
                                    class="model-item"
                                    :class="{ selected: isModelSelected(model.id, false) }"
                                    @click="selectModel(model.id, false)"
                                >
                                    <div class="model-content">
                                        <div class="model-header">
                                            <div class="model-name">{{ model.name }}</div>
                                            <span class="tier-badge default">Default</span>
                                            <span v-if="isModelSelected(model.id, false)" class="selected-badge">
                                                <i class="material-icons">check_circle</i> Seçili
                                            </span>
                                        </div>
                                        <div class="model-price">${{ formatPrice(model.defaultPrice, model.price) }}/dk</div>
                                    </div>
                                    <div class="model-actions">
                                        <a-tooltip v-if="model.realtime" placement="right">
                                            <template slot="title">
                                                <div>
                                                    <div>• Maksimum çağrı süresi: 30 dk</div>
                                                    <div>• Telaffuz sözlüğü: desteklenmiyor</div>
                                                    <div>• Konuşma normalizasyonu: desteklenmiyor</div>
                                                    <div>• Yedek ses kimliği: desteklenmiyor</div>
                                                </div>
                                            </template>
                                            <i class="material-icons info-icon">info</i>
                                        </a-tooltip>
                                    </div>
                                </div>

                                <!-- Fast Tier (if available) -->
                                <div
                                    v-if="model.hasFastTier"
                                    class="model-item fast-tier"
                                    :class="{ selected: isModelSelected(model.id, true) }"
                                    @click="selectModel(model.id, true)"
                                >
                                    <div class="model-content">
                                        <div class="model-header">
                                            <div class="model-name">{{ model.name }}</div>
                                            <span class="tier-badge fast">Fast</span>
                                            <span v-if="isModelSelected(model.id, true)" class="selected-badge fast">
                                                <i class="material-icons">check_circle</i> Seçili
                                            </span>
                                        </div>
                                        <div class="model-price">${{ formatPrice(model.fastPrice) }}/dk</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Claude Models -->
                    <div class="model-column">
                        <div class="column-header">
                            <div class="provider-logo-large">
                                <img src="https://static.supsis.live/common/image/claude-color.svg" alt="Claude" />
                            </div>
                            <h3 class="column-title">Claude</h3>
                        </div>
                        <div class="model-list">
                            <div
                                v-for="model in modelOptions.claude"
                                :key="model.id"
                                class="model-item"
                                :class="{ selected: isModelSelected(model.id, false) }"
                                @click="selectModel(model.id, false)"
                            >
                                <div class="model-content">
                                    <div class="model-header">
                                        <div class="model-name">{{ model.name }}</div>
                                        <span v-if="isModelSelected(model.id, false)" class="selected-badge">
                                            <i class="material-icons">check_circle</i> Seçili
                                        </span>
                                    </div>
                                    <div class="model-price">${{ formatPrice(model.defaultPrice, model.price) }}/dk</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Gemini Models -->
                    <div class="model-column">
                        <div class="column-header">
                            <div class="provider-logo-large">
                                <img src="https://static.supsis.live/common/image/gemini-color.svg" alt="Gemini" />
                            </div>
                            <h3 class="column-title">Gemini</h3>
                        </div>
                        <div class="model-list">
                            <div
                                v-for="model in modelOptions.gemini"
                                :key="model.id"
                                class="model-item"
                                :class="{ selected: isModelSelected(model.id, false) }"
                                @click="selectModel(model.id, false)"
                            >
                                <div class="model-content">
                                    <div class="model-header">
                                        <div class="model-name">{{ model.name }}</div>
                                        <span v-if="isModelSelected(model.id, false)" class="selected-badge">
                                            <i class="material-icons">check_circle</i> Seçili
                                        </span>
                                    </div>
                                    <div class="model-price">${{ formatPrice(model.defaultPrice, model.price) }}/dk</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import PermissionController from "../../../../../components/permission-controller/PermissionController";

export default {
    name: "LLMConfiguration",
    props: {
        agent: {
            type: Object,
            default: null,
        },
    },
    computed: {
        allModels() {
            return [
                ...this.modelOptions.openai,
                ...this.modelOptions.claude,
                ...this.modelOptions.gemini,
            ];
        },
        selectedModelName() {
            if (!this.agent || !this.agent.llm || !this.agent.llm.model) {
                return "Model Seçin";
            }
            const rawId = String(this.agent.llm.model).trim();
            const modelId = this.normalizeModelId(rawId);
            const selectedModel = this.allModels.find((m) => String(m.id).trim() === modelId);
            if (selectedModel) {
                const isFast = !!this.agent.llm.model_high_priority;
                const tierText = isFast && selectedModel.hasFastTier ? " (Fast)" : "";
                return selectedModel.name + tierText;
            }
            return this.formatModelIdAsLabel(rawId) || "Model Seçin";
        },
    },
    data() {
        return {
            isModelSelectorVisible: false,
            isSettingsVisible: false,
            // Yerel değerler
            localModelTemperature: 0,
            localModelHighPriority: false,
            localToolCallStrictMode: false,
            // Orijinal değerler
            originalValues: {
                modelTemperature: 0,
                modelHighPriority: false,
                toolCallStrictMode: false,
            },
            // Model listesi
            modelOptions: {
                openai: [
                    {
                        id: "gpt-5",
                        name: "GPT-5",
                        defaultPrice: 0.04 * 1.5,
                        fastPrice: 0.04 * 1.5 * 2,
                        hasFastTier: true,
                        icon: "openai",
                        provider: "OpenAI"
                    },
                    {
                        id: "gpt-5-mini",
                        name: "GPT-5 Mini",
                        defaultPrice: 0.012 * 1.5,
                        fastPrice: 0.012 * 1.5 * 2,
                        hasFastTier: true,
                        icon: "openai",
                        provider: "OpenAI"
                    },
                    {
                        id: "gpt-5-nano",
                        name: "GPT-5 Nano",
                        defaultPrice: 0.003 * 1.5,
                        fastPrice: null,
                        hasFastTier: false,
                        icon: "openai",
                        provider: "OpenAI"
                    },
                    {
                        id: "gpt-4.1",
                        name: "GPT-4.1",
                        defaultPrice: 0.045 * 1.5,
                        fastPrice: 0.045 * 1.5 * 2,
                        hasFastTier: false,
                        icon: "openai",
                        provider: "OpenAI"
                    },
                    {
                        id: "gpt-4.1-mini",
                        name: "GPT 4.1 mini",
                        price: 0.016 * 1.5,
                        description: "($0.024/min)",
                        icon: "psychology",
                    },
                    {
                        id: "gpt-4.1-nano",
                        name: "GPT 4.1 nano",
                        price: 0.006 * 1.5,
                        description: "($0.009/min)",
                        icon: "psychology",
                    },
                ],
                claude: [
                    {
                        id: "claude-4.5-sonnet",
                        name: "Claude 4.5 Sonnet",
                        defaultPrice: 0.06 * 1.5,
                        fastPrice: null,
                        hasFastTier: false,
                        icon: "anthropic",
                        provider: "Anthropic"
                    },
                    {
                        id: "claude-3.5-haiku",
                        name: "Claude 3.5 Haiku",
                        defaultPrice: 0.02 * 1.5,
                        fastPrice: null,
                        hasFastTier: false,
                        icon: "anthropic",
                        provider: "Anthropic"
                    }
                ],
                gemini: [
                    {
                        id: "gemini-2.5-flash",
                        name: "Gemini 2.5 Flash",
                        defaultPrice: 0.006 * 1.5,
                        fastPrice: null,
                        hasFastTier: false,
                        icon: "google",
                        provider: "Google"
                    },
                    {
                        id: "gemini-2.5-flash-lite",
                        name: "Gemini 2.5 Flash Lite",
                        defaultPrice: 0.003 * 1.5,
                        fastPrice: null,
                        hasFastTier: false,
                        icon: "google",
                        provider: "Google"
                    }
                ]
            },
        };
    },
    watch: {
        agent: {
            immediate: true,
            handler(newAgent) {
                if (newAgent && newAgent.llm) {
                    this.localModelTemperature =
                        newAgent.llm.model_temperature !== undefined ? newAgent.llm.model_temperature : 0;
                    this.localModelHighPriority = newAgent.llm.model_high_priority || false;
                    this.localToolCallStrictMode = newAgent.llm.tool_call_strict_mode || false;

                    // Orijinal değerleri güncelle
                    this.updateOriginalValues();
                }
            },
            deep: true,
        },
    },
    methods: {
        formatTemperature(val) {
            var num = Number(val);
            return (num != null && !isNaN(num) ? num : 0).toFixed(2);
        },
        formatPrice(primary, fallback) {
            var num = primary != null ? Number(primary) : (fallback != null ? Number(fallback) : 0);
            return (isNaN(num) ? 0 : num).toFixed(3);
        },
        openModelSelector() {
            if (!PermissionController.check(["voice-agent.update"])) {
                PermissionController.sendError("Bu işlemi gerçekleştirmek için yeterli izniniz yok.");
                return;
            }
            this.isModelSelectorVisible = true;
        },
        handleSettingsVisibleChange(visible) {
            this.isSettingsVisible = visible;
        },
        selectModel(modelId, isFastTier) {
            this.isModelSelectorVisible = false;

            // Agent'i güncelle
            this.$emit("update", {
                model: modelId,
                model_high_priority: isFastTier
            });
        },
        formatModelIdAsLabel(modelId) {
            if (!modelId || typeof modelId !== "string") return "";
            return modelId
                .split(/[-_\s]+/)
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                .join(" ");
        },
        normalizeModelId(apiModelId) {
            let id = String(apiModelId || "").trim().toLowerCase();
            if (id.includes("/")) id = id.replace(/^[^/]+\//, "");
            const legacyMap = {
                "claude-3.7-sonnet": "claude-4.5-sonnet",
                "gemini-2.0-flash": "gemini-2.5-flash",
                "gemini-2.0-flash-lite": "gemini-2.5-flash-lite",
            };
            return legacyMap[id] || id;
        },
        isModelSelected(modelId, isFastTier) {
            if (!this.agent || !this.agent.llm || this.agent.llm.model == null) return false;
            const currentModel = this.normalizeModelId(this.agent.llm.model);
            const compareId = String(modelId).trim().toLowerCase();
            const isModelMatch = currentModel === compareId;
            const isTierMatch = !!this.agent.llm.model_high_priority === !!isFastTier;
            return isModelMatch && isTierMatch;
        },
        isRealtimeModel(modelId) {
            const allModels = [...this.modelOptions.openai, ...this.modelOptions.claude, ...this.modelOptions.gemini];
            const model = allModels.find((m) => m.id === modelId);
            return model && model.realtime;
        },
        resetSettings() {
            // Değerleri orijinal haline geri döndür
            this.localModelTemperature = this.originalValues.modelTemperature;
            this.localModelHighPriority = this.originalValues.modelHighPriority;
            this.localToolCallStrictMode = this.originalValues.toolCallStrictMode;

            // Popover'ı kapat
            this.isSettingsVisible = false;
        },
        saveSettings() {
            // Değerleri güncelle
            this.$emit("update", {
                model_temperature: this.localModelTemperature,
                tool_call_strict_mode: this.localToolCallStrictMode,
            });

            // Orijinal değerleri güncelle
            this.updateOriginalValues();

            // Popover'ı kapat
            this.isSettingsVisible = false;
        },
        updateOriginalValues() {
            this.originalValues = {
                modelTemperature: this.localModelTemperature,
                modelHighPriority: this.localModelHighPriority,
                toolCallStrictMode: this.localToolCallStrictMode,
            };
        },
    },
};
</script>

<style scoped lang="scss">
.llm-configuration {
    .llm-button-group {
        display: flex;
        border: 1px solid #22272d;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

        .llm-selector-btn {
            flex: 1;
            padding: 12px 16px;
            cursor: pointer;
            background-color: #fff;
            border-right: 1px solid #22272d;
            transition: all 0.2s ease;

            &:hover {
                background-color: #f8f9fa;
            }

            .btn-content {
                display: flex;
                justify-content: space-between;
                align-items: center;

                .model-info {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .model-icon {
                        color: #22272d;
                        font-size: 20px;
                    }

                    .model-name {
                        font-weight: 500;
                        font-size: 14px;
                    }
                }
            }
        }

        .llm-settings-btn {
            padding: 12px 16px;
            cursor: pointer;
            background-color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;

            &:hover {
                background-color: #f8f9fa;
            }

            .material-icons {
                font-size: 20px;
                color: #595959;
            }
        }
    }
}

.model-selector {
    .model-columns {
        display: flex;
        gap: 24px;

        .model-column {
            flex: 1;

            .column-header {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 20px;
                padding-bottom: 12px;
                border-bottom: 2px solid #f0f0f0;

                .provider-logo-large {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 32px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    padding: 4px;

                    img {
                        width: 24px;
                        height: 24px;
                        object-fit: contain;
                    }
                }

                .column-title {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: #22272d;
                }
            }

            .model-list {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .model-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px;
                    border: 2px solid #e8e8e8;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                    position: relative;
                    overflow: hidden;

                    &::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: linear-gradient(135deg, rgba(34, 39, 45, 0.02) 0%, rgba(34, 39, 45, 0.05) 100%);
                        opacity: 0;
                        transition: opacity 0.3s ease;
                    }

                    &:hover {
                        border-color: #22272d;
                        transform: translateY(-2px);
                        box-shadow: 0 8px 25px rgba(34, 39, 45, 0.15);

                        &::before {
                            opacity: 1;
                        }
                    }

                    &.selected {
                        border-width: 3px;
                        border-color: #1890ff;
                        background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
                        box-shadow: 0 4px 24px rgba(24, 144, 255, 0.35), inset 0 0 0 1px rgba(24, 144, 255, 0.1);

                        &::before {
                            background: linear-gradient(135deg, rgba(24, 144, 255, 0.08) 0%, rgba(24, 144, 255, 0.15) 100%);
                            opacity: 1;
                        }
                    }

                    &.fast-tier {
                        background: linear-gradient(135deg, #fff2e8 0%, #fff7f0 100%);

                        &:hover {
                            background: linear-gradient(135deg, #ffe7d3 0%, #fff2e8 100%);
                        }

                        &.selected {
                            border-width: 3px;
                            border-color: #ff4d4f;
                            background: linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%);
                            box-shadow: 0 4px 24px rgba(255, 77, 79, 0.35), inset 0 0 0 1px rgba(255, 77, 79, 0.1);
                        }
                    }

                    .model-content {
                        flex: 1;

                        .model-header {
                            display: flex;
                            align-items: center;
                            flex-wrap: wrap;
                            gap: 8px;
                            margin-bottom: 4px;

                            .model-name {
                                font-weight: 600;
                                font-size: 14px;
                                color: #22272d;
                            }

                            .tier-badge {
                                font-size: 9px;
                                padding: 3px 8px;
                                border-radius: 12px;
                                font-weight: 700;
                                text-transform: uppercase;
                                letter-spacing: 0.5px;

                                &.default {
                                    background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%);
                                    color: #666;
                                    border: 1px solid #d9d9d9;
                                }

                                &.fast {
                                    background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
                                    color: white;
                                    box-shadow: 0 2px 8px rgba(255, 77, 79, 0.3);
                                }
                            }

                            .selected-badge {
                                display: inline-flex;
                                align-items: center;
                                gap: 4px;
                                padding: 2px 8px;
                                border-radius: 6px;
                                font-size: 12px;
                                font-weight: 600;
                                color: #1890ff;
                                background: rgba(24, 144, 255, 0.2);

                                .material-icons {
                                    font-size: 16px;
                                }

                                &.fast {
                                    color: #ff4d4f;
                                    background: rgba(255, 77, 79, 0.2);
                                }
                            }
                        }

                        .model-price {
                            color: #8c8c8c;
                            font-size: 12px;
                            font-weight: 600;
                        }
                    }

                    .model-actions {
                        .info-icon {
                            color: #1890ff;
                            font-size: 18px;
                            opacity: 0.7;
                            transition: opacity 0.2s ease;

                            &:hover {
                                opacity: 1;
                            }
                        }
                    }
                }
            }
        }
    }
}

.llm-settings {
    padding: 20px;
    min-width: 300px;

    .settings-title {
        margin: 0 0 20px 0;
        font-size: 16px;
        font-weight: 600;
        color: #22272d;
    }

    .settings-section {
        margin-bottom: 24px;

        .section-title {
            margin: 0 0 12px 0;
            font-size: 14px;
            font-weight: 500;
            color: #22272d;
        }

        .slider-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;

            .section-title {
                margin: 0;
            }

            .value {
                font-weight: 600;
                color: #1890ff;
                background: #e6f7ff;
                padding: 4px 8px;
                border-radius: 6px;
                font-size: 12px;
            }
        }

        .slider-description,
        .setting-description {
            margin-top: 8px;
            font-size: 12px;
            color: #8c8c8c;
            line-height: 1.4;
        }

        .setting-checkbox {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
        }
    }

    .action-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 20px;
        padding-top: 16px;
        border-top: 1px solid #f0f0f0;
    }
}

:deep(.llm-settings-popover) {
    min-width: 320px;
}

:deep(.ant-modal-content) {
    border-radius: 16px;
    overflow: hidden;
}

:deep(.ant-modal-header) {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-bottom: 2px solid #f0f0f0;
    padding: 20px 24px;

    .ant-modal-title {
        font-weight: 600;
        font-size: 18px;
        color: #22272d;
    }
}

:deep(.ant-modal-body) {
    padding: 24px;
}
</style>

<!-- Modal body'de render edildiğinde stiller (scoped uygulanmadığı için global); seçili çok belirgin -->
<style lang="scss">
/* Modal içeriği body'de olsa da .llm-model-selector-modal-content sınıfı doğrudan içerik div'inde */
.llm-model-selector-modal-content {
        .model-columns {
            display: flex;
            gap: 24px;
        }
        .model-column {
            flex: 1;
        }
        .column-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
            padding-bottom: 12px;
            border-bottom: 2px solid #f0f0f0;
        }
        .provider-logo-large {
            width: 32px;
            height: 32px;
            background: #f8f9fa;
            border-radius: 8px;
            padding: 4px;
        }
        .column-header img {
            width: 24px;
            height: 24px;
            object-fit: contain;
        }
        .column-title {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #22272d;
        }
        .model-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        .model-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            border: 2px solid #e8e8e8;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
            position: relative;
            overflow: hidden;
        }
        .model-item:hover {
            border-color: #22272d;
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(34, 39, 45, 0.15);
        }
        /* Seçili model: sol çizgi + koyu arka plan + gölge (hem wrapClassName hem doğrudan içerikte çalışır) */
        .model-item.selected {
            border-color: #1890ff !important;
            border-width: 2px !important;
            border-left: 4px solid #1890ff !important;
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(24, 144, 255, 0.35) !important;
            background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%) !important;
        }
        .model-item.fast-tier {
            background: linear-gradient(135deg, #fff2e8 0%, #fff7f0 100%);
        }
        .model-item.fast-tier:hover {
            background: linear-gradient(135deg, #ffe7d3 0%, #fff2e8 100%);
        }
        .model-item.fast-tier.selected {
            border-color: #ff4d4f !important;
            border-left: 4px solid #ff4d4f !important;
            background: linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%) !important;
            box-shadow: 0 8px 25px rgba(255, 77, 79, 0.3) !important;
        }
        .model-content {
            flex: 1;
        }
        .model-header {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 4px;
        }
        .model-name {
            font-weight: 600;
            font-size: 14px;
            color: #22272d;
        }
        .tier-badge {
            font-size: 9px;
            padding: 3px 8px;
            border-radius: 12px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        .tier-badge.default {
            background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%);
            color: #666;
            border: 1px solid #d9d9d9;
        }
        .tier-badge.fast {
            background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
            color: white;
            box-shadow: 0 2px 8px rgba(255, 77, 79, 0.3);
        }
        .selected-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 2px 8px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 600;
            color: #1890ff;
            background: rgba(24, 144, 255, 0.2);
        }
        .selected-badge .material-icons {
            font-size: 16px;
        }
        .selected-badge.fast {
            color: #ff4d4f;
            background: rgba(255, 77, 79, 0.2);
        }
        .model-price {
            color: #8c8c8c;
            font-size: 12px;
            font-weight: 600;
        }
        .model-actions .info-icon {
            color: #1890ff;
            font-size: 18px;
            opacity: 0.7;
        }
}
</style>
